<template>
    <section id="bulletslide" ref="sectionRef"
        class="introslide relative flex flex-col justify-start lg:justify-center min-h-screen bg-workbook-white before:hidden before:content-[''] lg:before:block lg:before:absolute lg:before:inset-y-0 lg:before:left-0 lg:before:w-5/12 lg:before:bg-workbook-black">
        <UContainer class="relative z-10 px-0! py-0">
            <UPageGrid class="m-0">
                <div
                    class="headersection bg-workbook-black lg:h-1/2 col-span-full lg:col-span-5 min-h-[50vh] lg:min-h-screen flex flex-col justify-start lg:justify-center">
                    <div
                        class="h-full flex flex-col justify-center lg:bg-transparent w-screen  lg:w-auto max-w-none p-6 lg:px-0 relative left-1/2 -translate-x-1/2 lg:static lg:translate-x-0">
                        <h1 class="mb-12 lg:mb-12 md:pr-20 text-workbook-orange">
                            {{ props.headline }}
                        </h1>

                    </div>
                </div>
                <div
                    class="col-span-full lg:col-span-7 min-h-[50vh] lg:min-h-screen flex items-center justify-center px-6 lg:px-0 lg:justify-start lg:pl-20 lg:pr-8 pb-8 lg:pb-0">
                    <div class="slot">
                        <slot />
                    </div>
                </div>
            </UPageGrid>
        </UContainer>
    </section>
</template>

<script setup lang="ts">

import { onMounted, ref, computed } from 'vue'
import { useIntroStore } from '@/store/intro'
import { useWindowSize } from '@vueuse/core'

import { loadGsap } from '@/utils/gsap'
import IntroSlideFive from './IntroSlideFive.vue'

const isDev = import.meta.dev

const introStore = useIntroStore()
const { width } = useWindowSize()

const sectionRef = ref<HTMLElement | null>(null)
const isMobile = computed(() => width.value < 800)

let ctx: any | null = null

const props = defineProps({
    headline: {
        type: String,
        default: ''
    }
})

const contentIndex = ref(0);
const timeline = ref<any>(null);
const textFadeTimeline = ref<any>(null);

onBeforeMount(() => {
    // Reset slides on before mount to ensure clean state
    introStore.resetSlides();
})

onBeforeUnmount(() => {
    // Kill ScrollTrigger instances before unmounting to prevent performance violations
    if (timeline.value) {
        timeline.value.kill();
        timeline.value = null;
    }
    if (textFadeTimeline.value) {
        textFadeTimeline.value.kill();
        textFadeTimeline.value = null;
    }
    ctx?.revert()
    ctx = null
});

onMounted(async () => {
    let index = introStore.mountedSlides.length
    contentIndex.value = index;
    if (sectionRef.value) {
        introStore.registerSlide(sectionRef.value)
    }


    if (process.client) {
        const { gsap, SplitText, ScrollTrigger } = await loadGsap()

        ctx = gsap.context(() => {

            const slot = sectionRef.value?.querySelector('.slot')


            
            const scrollEnd = computed(() => "bottom 0%")

            let tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.value,
                    pin: false,
                    markers: false,
                    start: isMobile.value ? "top 20%" : "top 70%",
                    end: isMobile.value ? "bottom 20%" : "bottom 70%",
                    scrub: true
                }
            })


            if (slot) {

                
                const listItems = Array.from(slot.querySelectorAll('li'))

                listItems.forEach((item) => {
                    const textTarget = item.querySelector('.bullet-text') ?? item
                    const split = SplitText.create(textTarget, { type: "words", aria: false })
                    const icon = item.querySelector('.bullet')
                    const linePause = 1.5

                    if (icon) {
                        
                        tl.fromTo(icon, {scale: 0}, {
                            duration: 1,
                            scale: 1,
                            ease: "back.out(1.7)"
                        }, "+=5")
                    }
                    tl.from(split.words, {
                        duration: 2,
                        x: 10,
                        autoAlpha: 0,
                        stagger: .2
                    }, icon ? "+=2" : "+=0")
                    tl.to(item, { duration: linePause, opacity: 1, ease: "none" })

                })
                
                tl.addLabel("end")
                if (isMobile.value) {
                    tl.to(slot, { y: 70, opacity: 0, duration: 10 }, "+=10")
                }
            }
        }, sectionRef.value || undefined)

        

    }
})
</script>

<style scoped>
@reference "../../assets/css/main.css";

:deep(.slot li) {
    list-style: none;
    position: relative;

    @apply text-lg lg:text-4xl leading-loose
}
</style>
